import React, { useEffect, useState } from "react";
import {
  Dialog,
  IconButton,
  DialogContent,
  Typography,
  Box,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Checkbox,
  TableSortLabel,
  Link,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { SkillsMapWorkflowDialogProps, UnMapWorkFlowToUsers } from "../../models/SkillsData";
import { useTranslation } from "react-i18next";
import { useMapAndAssignUtils } from "../../models/SkillsConstants";
import {
  SortObj,
} from "../../components/skills-management/components/map-workflow/models/MapWorkFlowToUsers";
import { TableHeadCell } from "../../../../shared/models/TableModels";
import { visuallyHidden } from "@mui/utils";
import { TableLoading } from "../../../../shared/components/skeleton/table-loading/TableLoading";
import { useAPI } from "../../../../shared/services/api/API";
import { OPTIVAL_CONSTANTS } from "../../../../shared/Constants";
import { updateURLParams } from "../../../../shared/utils/Utils";

export const MapWorkflowDialog = (Props: SkillsMapWorkflowDialogProps) => {
  const { isOpen, closeDialog, skillLevelId, skillId, skillName } = Props;
  const { t } = useTranslation();
  const { httpGet, httpPost, handleAlertBar } = useAPI();
  const [searchText, setSearchText] = useState("");
  const { getMapWorkFlowHeaders } = useMapAndAssignUtils();
  const [unMapSort, setUnMapSort] = useState<SortObj>({
    order: "desc",
    orderBy: "name",
  });
  const [unMappedWorkFlow, setUnMappedWorkFlow] = useState<
    UnMapWorkFlowToUsers[]
  >([]);
  const [displayUnMappedWorkFlow, setDisplayUnMappedWorkFlow] = useState<
    UnMapWorkFlowToUsers[]
  >([]);
  const [checkedUnMappedWorkFlow, setCheckedUnMappedWorkFlow] = useState<
    UnMapWorkFlowToUsers[]
  >([]);
  const [unMappedPagination, setUnMappedPagination] = useState({
    page: 0,
    rowsPerPage: 5,
  });
  const [isLoadingUnMapped, setIsLoadingUnMapped] = useState(true);
  const handleSorting = (property: string, table: string) => {
    if (table === "unMappedTable") {
      const isAsc = unMapSort.orderBy === property && unMapSort.order === "asc";
      setUnMapSort({ order: isAsc ? "desc" : "asc", orderBy: property });
    }
  };
  const handleChangePageForUnMappedWorkFlow = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setUnMappedPagination({
      page: newPage,
      rowsPerPage: unMappedPagination.rowsPerPage,
    });
    const startIndex = newPage * unMappedPagination.rowsPerPage;
    setDisplayUnMappedWorkFlow([
      ...unMappedWorkFlow.slice(
        startIndex,
        startIndex + unMappedPagination.rowsPerPage
      ),
    ]);
  };

  const handleChangeRowsPerPageForUnMappedWorkFlow = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setUnMappedPagination({
      page: 0,
      rowsPerPage: parseInt(event.target.value, 10),
    });
    setDisplayUnMappedWorkFlow(
      unMappedWorkFlow.slice(0, parseInt(event.target.value, 10))
    );
  };
  const checkedUnMap = (data: any): boolean => {
    if (!checkedUnMappedWorkFlow.length) {
      return false;
    }
    const arr = checkedUnMappedWorkFlow.filter(
      (res: any) => res.id === data.id
    );
    return arr.length > 0 ? true : false;
  };

  const handleCheckBoxForUnMap = (event: any, data: any) => {
    if (event.target.checked) {
      setCheckedUnMappedWorkFlow([...checkedUnMappedWorkFlow, data]);
    } else {
      const index = checkedUnMappedWorkFlow.findIndex(
        (workflow: any) => workflow.id === data.id
      );
      checkedUnMappedWorkFlow.splice(index, 1);

      setCheckedUnMappedWorkFlow([...checkedUnMappedWorkFlow]);
    }
  };
  function areAllObjectsPresentForUnMap() {
    if (
      checkedUnMappedWorkFlow.length === 0 &&
      displayUnMappedWorkFlow.length === 0
    )
      return false;
    for (let workflow of displayUnMappedWorkFlow) {
      const isPresent = checkedUnMappedWorkFlow.some(
        (data: any) => data.id === workflow.id
      );

      if (!isPresent) {
        return false;
      }
    }
    return true;
  }
  const handleAllCheckBoxForUnMapped = (event: any) => {
    if (event.target.checked) {
      const uniqueWorkFlows: UnMapWorkFlowToUsers[] = [];
      displayUnMappedWorkFlow.forEach((workflow: UnMapWorkFlowToUsers) => {
        const index = checkedUnMappedWorkFlow.findIndex(
          (checkedUnWorkFlow: UnMapWorkFlowToUsers) =>
            checkedUnWorkFlow.id === workflow.id
        );
        if (index === -1) {
          uniqueWorkFlows.push(workflow);
        }
      });
      setCheckedUnMappedWorkFlow([
        ...checkedUnMappedWorkFlow,
        ...uniqueWorkFlows,
      ]);
    } else {
      setCheckedUnMappedWorkFlow([]);
    }
  };
  const queryParams = {
    page: 0,
    size: 1000,
    sortColumn: "modifiedAt",
    isDescending: true,
    searchString: searchText,
  };
  const getUnMappedSkillsWorkFlow = async () => {
    setIsLoadingUnMapped(true);
    const urlParams = {
      skillId: skillId,
    };
    await httpGet(
      updateURLParams(
        OPTIVAL_CONSTANTS.API_URLS.GET_UNMAPPED_SKILLS_WORKFLOW,
        urlParams
      ),
      queryParams
    ).then(
      (response: any) => {
        if (response && response.data) {
          if (response.data.error) {
            handleAlertBar(
              "error",
              response?.data?.message || t("opva.serviceUnavailable")
            );
            setIsLoadingUnMapped(false);
          } else {
            setUnMappedWorkFlow(response.data);
            setDisplayUnMappedWorkFlow(
              response.data.slice(0, unMappedPagination.rowsPerPage)
            );
            setIsLoadingUnMapped(false);
            handleEmptyPage(response.data, "unMappedUser");
          }
        } else {
          handleAlertBar("error", t("opva.serviceUnavailable"));
          setIsLoadingUnMapped(false);
        }
      },
      (error: any) => {
        setIsLoadingUnMapped(false);
      }
    );
  };

  useEffect(() => {
    if (isOpen) {
      getUnMappedSkillsWorkFlow();
    }
  }, [isOpen]);

  const handleEmptyPage = (data: any, table: string) => {
    if (table === "unMappedUser") {
      const sliceArr = data.slice(
        unMappedPagination.page * unMappedPagination.rowsPerPage,
        unMappedPagination.page * unMappedPagination.rowsPerPage +
          unMappedPagination.rowsPerPage
      );
      if (!sliceArr.length) {
        setUnMappedPagination({
          page: unMappedPagination.page === 0 ? 0 : unMappedPagination.page - 1,
          rowsPerPage: unMappedPagination.rowsPerPage,
        });
      }
    }
  };

  const handleMapWorkFlow = async () => {
    const arr = checkedUnMappedWorkFlow.map((workflow: any) => {
      return workflow.id;
    });
    const requestBody = {
      workflowIds: arr,
      skillId: skillId,
      skillLevelId: skillLevelId,
    };
    await httpPost(
      OPTIVAL_CONSTANTS.API_URLS.MAP_SKILLS_TO_WORKFLOW,
      requestBody
    ).then(
      (response: any) => {
        if (response && response.status) {
          if (response.status === 200) {
            handleAlertBar(
              "success",
              t("opva.skillWorkflowMappedSuccessfully")
            );
            getUnMappedSkillsWorkFlow();
            setCheckedUnMappedWorkFlow([]);
            closeDialog();
          } else {
            handleAlertBar(
              "error",
              response?.data?.message || t("opva.serviceUnavailable")
            );
          }
        } else {
          handleAlertBar("error", t("opva.serviceUnavailable"));
        }
      },
      (error: any) => {}
    );
  };
  return (
    <Dialog fullWidth open={isOpen} onClose={closeDialog} maxWidth="lg">
      <DialogContent>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h5">{t("opva.availableWorkflow")}</Typography>
          <IconButton aria-label="close" onClick={closeDialog} color="primary">
            <CloseIcon />
          </IconButton>
        </div>
        <Box>
          <TableContainer className="table-container">
            <Table stickyHeader size="medium" aria-label="UnMapWorkflow table">
              <TableHead>
                <TableRow key="mapworkflow-table-header">
                  <TableCell size="small" padding="checkbox">
                    <Checkbox
                      checked={areAllObjectsPresentForUnMap()}
                      onChange={(e) => handleAllCheckBoxForUnMapped(e)}
                    />
                  </TableCell>
                  {getMapWorkFlowHeaders().map((headCell: TableHeadCell) => (
                    <TableCell
                      key={headCell.id}
                      sortDirection={
                        unMapSort.orderBy === headCell.id
                          ? unMapSort.order
                          : false
                      }
                      align={headCell.position}
                      className="fw-bold"
                    >
                      {headCell?.sorting ? (
                        <TableSortLabel
                          active={
                            unMapSort.orderBy === headCell.sortingProperty
                          }
                          direction={
                            unMapSort.orderBy === headCell.sortingProperty
                              ? unMapSort.order
                              : "desc"
                          }
                          onClick={(event) =>
                            handleSorting(
                              headCell.sortingProperty,
                              "unMappedTable"
                            )
                          }
                        >
                          {headCell.label}
                          {unMapSort.orderBy === headCell.id ? (
                            <Box component="span" sx={visuallyHidden}>
                              {unMapSort.order === "desc"
                                ? "sorted descending"
                                : "sorted ascending"}
                            </Box>
                          ) : null}
                        </TableSortLabel>
                      ) : (
                        headCell.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              {isLoadingUnMapped ? (
                <TableLoading column={getMapWorkFlowHeaders().length + 1} />
              ) : (
                <TableBody>
                  {unMappedWorkFlow && unMappedWorkFlow.length ? (
                    displayUnMappedWorkFlow.map((data: UnMapWorkFlowToUsers) => (
                      <TableRow key={data.id}>
                        <TableCell size="small" padding="checkbox">
                          <Checkbox
                            checked={checkedUnMap(data)}
                            onChange={(e) => handleCheckBoxForUnMap(e, data)}
                          />
                        </TableCell>
                        <TableCell align="left">{data.name}</TableCell>
                        <TableCell align="left">{data.createdBy}</TableCell>
                        <TableCell>
                          {data?.assignedTo?.groups?.length > 0
                            ? data?.assignedTo?.groups?.map((user: any, index: number) => (
                              user + (index < data?.assignedTo?.groups.length - 1 ? ', ' : '')
                            )): '-'}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell align="center" colSpan={4}>
                        {searchText
                          ? t("opva.searchUnmappedWorkflowDataNotAvailable")
                          : t("opva.noDataAvailable")}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              )}
            </Table>
            <div className="d-flex align-items-center justify-content-end">
              <TablePagination
                component="div"
                className="pagination-container mb-2"
                rowsPerPageOptions={[5, 10, 25]}
                count={unMappedWorkFlow.length}
                page={unMappedPagination.page}
                onPageChange={handleChangePageForUnMappedWorkFlow}
                rowsPerPage={unMappedPagination.rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPageForUnMappedWorkFlow}
              />
            </div>
          </TableContainer>
          <div className="pb-2">
            <Button
              variant="contained"
              color="primary"
              onClick={handleMapWorkFlow}
              disabled={!checkedUnMappedWorkFlow.length}
            >
              {t("opva.mapWorkflowUpperCase")}
            </Button>
          </div>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
